import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/**
 * Multi-step signup (Phase C).
 *  Step 1 — Account (name / email / password)
 *  Step 2 — Company (company_name / industry)
 *  Step 3 — Usage Type
 *  Step 4 — Scale
 *  Step 5 — Branding (primary_color)
 *
 *  Account creation hits POST /api/auth/register at the end of step 1 (we
 *  need a token to call /me/onboarding). Steps 2-5 fill out the signup blob
 *  in a single POST /api/me/onboarding call at submit time. This keeps the
 *  server contract small and idempotent.
 */
const STEPS = [
    { key: "account",  label: "Account" },
    { key: "company",  label: "Company" },
    { key: "usage",    label: "Usage" },
    { key: "scale",    label: "Scale" },
    { key: "branding", label: "Branding" },
];

const USAGE_TYPES = [
    { value: "freelancer", label: "Freelancer", desc: "Solo creator producing for clients on the side." },
    { value: "agency",     label: "Agency",     desc: "Boutique creative studio shipping multiple campaigns." },
    { value: "in_house",   label: "In-house",   desc: "Internal marketing / brand team at a company." },
    { value: "studio",     label: "Production studio", desc: "Full-stack production house with a roster." },
];

const SCALE_OPTIONS = [
    { value: "solo",   label: "Solo",       desc: "Just me." },
    { value: "small",  label: "2 – 10",     desc: "Small team." },
    { value: "medium", label: "11 – 50",    desc: "Mid-size studio." },
    { value: "large",  label: "50+",        desc: "Enterprise scale." },
];

const COLOR_PRESETS = ["#FF4400", "#22D3EE", "#A78BFA", "#10B981", "#F59E0B", "#EC4899"];

export default function Signup() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(false);   // becomes true after step 1
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        company_name: "",
        industry: "",
        usage_type: "agency",
        scale: "small",
        primary_color: "#FF4400",
    });

    if (user) return <Navigate to="/dashboard" replace />;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const validateStep = () => {
        if (step === 0) {
            if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
                toast.error("Fill name, email, and a 6+ char password.");
                return false;
            }
        }
        if (step === 1 && !form.company_name.trim()) {
            toast.error("Company name helps the CEO Agent personalise."); return false;
        }
        return true;
    };

    const next = async () => {
        if (!validateStep()) return;
        // Step 0 — register the account; we need a token to save onboarding.
        if (step === 0 && !created) {
            setBusy(true);
            try {
                const { data } = await api.post("/auth/register", {
                    email: form.email.trim(),
                    password: form.password,
                    name: form.name.trim(),
                    role: "owner",
                });
                localStorage.setItem("aivos_token", data.token);
                localStorage.setItem("aivos_user", JSON.stringify(data.user));
                setCreated(true);
                toast.success("Account created.");
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Registration failed.");
                setBusy(false);
                return;
            }
            setBusy(false);
        }
        setStep(s => Math.min(s + 1, STEPS.length - 1));
    };

    const prev = () => setStep(s => Math.max(0, s - 1));

    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/me/onboarding", {
                company_name: form.company_name.trim(),
                industry: form.industry.trim(),
                usage_type: form.usage_type,
                scale: form.scale,
                primary_color: form.primary_color,
            });
            // Re-login (also refreshes is_admin / status / must_change_password).
            await login(form.email.trim(), form.password);
            // Brand-new accounts land in `pending` status — bounce them to
            // the Awaiting Approval screen. The route guard would do this
            // anyway; explicit nav makes the redirect deterministic.
            toast.success("Profile saved. Your account is awaiting admin approval.");
            nav("/pending");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Onboarding save failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="signup-page">
            <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-[#FF4400] flex items-center justify-center font-bold">A</div>
                    <div className="font-display text-base">Furmais</div>
                </Link>
                <Link to="/login" className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white">
                    Sign in →
                </Link>
            </header>

            <div className="max-w-2xl mx-auto px-6 sm:px-10 pt-6 pb-16">
                {/* Stepper */}
                <ol className="flex items-center gap-2 mb-10" data-testid="signup-stepper">
                    {STEPS.map((s, i) => {
                        const state = i < step ? "done" : i === step ? "current" : "todo";
                        return (
                            <li key={s.key} className="flex-1 flex items-center gap-2 min-w-0" data-testid={`step-${s.key}`}>
                                <div className={
                                    "w-7 h-7 rounded-full grid place-items-center text-[11px] font-mono shrink-0 " +
                                    (state === "done"    ? "bg-[#FF4400] text-white" :
                                     state === "current" ? "border border-[#FF4400] text-white" :
                                                           "border border-zinc-800 text-zinc-600")
                                }>
                                    {state === "done" ? <Check className="w-3.5 h-3.5" /> : i + 1}
                                </div>
                                <div className="text-[11px] font-mono uppercase tracking-wider truncate"
                                     style={{ color: state === "todo" ? "#52525b" : state === "current" ? "#fff" : "#a1a1aa" }}>
                                    {s.label}
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div className="flex-1 h-px bg-zinc-900 ml-1" />
                                )}
                            </li>
                        );
                    })}
                </ol>

                {/* Step body */}
                <div className="card-base p-6 sm:p-8">
                    {step === 0 && (
                        <StepAccount form={form} set={set} />
                    )}
                    {step === 1 && (
                        <StepCompany form={form} set={set} />
                    )}
                    {step === 2 && (
                        <StepUsage form={form} set={set} />
                    )}
                    {step === 3 && (
                        <StepScale form={form} set={set} />
                    )}
                    {step === 4 && (
                        <StepBranding form={form} set={set} />
                    )}

                    <div className="mt-8 flex items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={prev}
                            disabled={step === 0}
                            data-testid="signup-prev"
                            className="text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button
                                type="button"
                                onClick={next}
                                disabled={busy}
                                data-testid="signup-next"
                                className="btn-accent text-sm"
                            >
                                {busy ? "..." : "Continue"} <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={submit}
                                disabled={busy}
                                data-testid="signup-finish"
                                className="btn-accent text-sm"
                            >
                                {busy ? "..." : "Finish"} <Check className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <Toaster theme="dark" />
        </div>
    );
}

/* ──────────────── per-step components ──────────────── */

function StepAccount({ form, set }) {
    return (
        <div>
            <div className="label-micro mb-1">Step 1 of 5</div>
            <h2 className="font-display text-2xl mb-1">Create your account</h2>
            <p className="text-sm text-zinc-500 mb-6">Email + password. Used for sign-in only.</p>
            <Field label="Your name">
                <input
                    data-testid="signup-name"
                    value={form.name} onChange={e => set("name", e.target.value)}
                    className="form-input" placeholder="e.g. Priya Verma"
                />
            </Field>
            <Field label="Work email">
                <input
                    data-testid="signup-email"
                    type="email" value={form.email}
                    onChange={e => set("email", e.target.value)}
                    className="form-input" placeholder="you@studio.com"
                />
            </Field>
            <Field label="Password (6+ chars)">
                <input
                    data-testid="signup-password"
                    type="password" value={form.password}
                    onChange={e => set("password", e.target.value)}
                    className="form-input" placeholder="••••••"
                />
            </Field>
        </div>
    );
}

function StepCompany({ form, set }) {
    return (
        <div>
            <div className="label-micro mb-1">Step 2 of 5</div>
            <h2 className="font-display text-2xl mb-1">Your company</h2>
            <p className="text-sm text-zinc-500 mb-6">Used by the CEO Agent to personalise greetings and decks.</p>
            <Field label="Company name">
                <input
                    data-testid="signup-company"
                    value={form.company_name} onChange={e => set("company_name", e.target.value)}
                    className="form-input" placeholder="e.g. RIFA Productions"
                />
            </Field>
            <Field label="Industry (optional)">
                <input
                    data-testid="signup-industry"
                    value={form.industry} onChange={e => set("industry", e.target.value)}
                    className="form-input" placeholder="e.g. Creative Agency, D2C, Real Estate"
                />
            </Field>
        </div>
    );
}

function StepUsage({ form, set }) {
    return (
        <div>
            <div className="label-micro mb-1">Step 3 of 5</div>
            <h2 className="font-display text-2xl mb-1">How will you use this?</h2>
            <p className="text-sm text-zinc-500 mb-6">Pick the closest match — you can change it later.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {USAGE_TYPES.map(o => (
                    <OptionCard
                        key={o.value}
                        testid={`signup-usage-${o.value}`}
                        active={form.usage_type === o.value}
                        onClick={() => set("usage_type", o.value)}
                        title={o.label}
                        desc={o.desc}
                    />
                ))}
            </div>
        </div>
    );
}

function StepScale({ form, set }) {
    return (
        <div>
            <div className="label-micro mb-1">Step 4 of 5</div>
            <h2 className="font-display text-2xl mb-1">Team size</h2>
            <p className="text-sm text-zinc-500 mb-6">Helps us recommend the right plan.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {SCALE_OPTIONS.map(o => (
                    <OptionCard
                        key={o.value}
                        testid={`signup-scale-${o.value}`}
                        active={form.scale === o.value}
                        onClick={() => set("scale", o.value)}
                        title={o.label}
                        desc={o.desc}
                    />
                ))}
            </div>
        </div>
    );
}

function StepBranding({ form, set }) {
    return (
        <div>
            <div className="label-micro mb-1">Step 5 of 5</div>
            <h2 className="font-display text-2xl mb-1">Pick a brand accent</h2>
            <p className="text-sm text-zinc-500 mb-6">Used on your deliverables and dashboard. Change anytime in Settings.</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
                {COLOR_PRESETS.map(c => (
                    <button
                        key={c}
                        type="button"
                        data-testid={`signup-color-${c.replace("#", "")}`}
                        onClick={() => set("primary_color", c)}
                        className={
                            "h-14 rounded-sm border-2 transition-all " +
                            (form.primary_color === c ? "border-white scale-105" : "border-zinc-900 hover:border-zinc-700")
                        }
                        style={{ background: c }}
                        aria-label={`Use color ${c}`}
                    />
                ))}
            </div>
            <Field label="Custom hex (optional)">
                <input
                    value={form.primary_color}
                    data-testid="signup-color-custom"
                    onChange={e => set("primary_color", e.target.value)}
                    className="form-input font-mono"
                    placeholder="#FF4400"
                />
            </Field>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="mb-4">
            <label className="label-micro block mb-2">{label}</label>
            {children}
        </div>
    );
}

function OptionCard({ active, onClick, title, desc, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={
                "text-left p-4 rounded-sm border transition-colors " +
                (active
                    ? "border-[#FF4400] bg-[#FF4400]/5"
                    : "border-zinc-900 hover:border-zinc-700")
            }
        >
            <div className="font-medium text-sm">{title}</div>
            <div className="text-xs text-zinc-500 mt-1">{desc}</div>
        </button>
    );
}
